import React from 'react'
import classNames from 'classnames'

import { createStyles, withStyles, WithStyles, Theme } from '@material-ui/core/styles'

import { IStorePremiumProduct, StorePremiumProduct } from 'protos'

import Grid from '@material-ui/core/Grid'
import CloseIcon from '@material-ui/icons/Close'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'

import T from 'components/FormattedMessage'

import MoreInfoButton from './MoreInfoButton'
import NavigateToStoreButton from './NavigateToStoreButton'
import UpgradeMembershipButton from './UpgradeMembershipButton'
import PremiumMembershipDetails from './PremiumMembershipDetails'

import defaultBackground from './assets/background_premium_promo.jpg'
import silverBackground from './assets/background_premium_silver.jpg'
import goldBackground from './assets/background_premium_gold.jpg'

export interface Props {
  onClose?: (event: React.SyntheticEvent<HTMLElement>) => void
  className?: string
  isDetailed?: boolean
  membership?: IStorePremiumProduct
  isShowExtend?: boolean
  activeMembership?: IStorePremiumProduct | null
}

const styles = (theme: Theme) => createStyles({
  root: {
    padding: theme.spacing() * 3,
    backgroundSize: 'cover',
    backgroundPositionX: 'center',
    backgroundPositionY: 'top',
    backgroundRepeat: 'no-repeat',
  },
  detailed: {
    height: '100%',
    overflow: 'hidden',
    overflowY: 'auto',
    backgroundSize: 'auto 70%, contain',
  },
  default: {
    backgroundImage: `url(${defaultBackground})`,
    backgroundColor: theme.components.PremiumMembership.backgroundColor.default,
    color: theme.components.PremiumMembership.color.default,
  },
  defaultDetailed: {
    backgroundImage: `
      linear-gradient(
        transparent,
        ${theme.components.PremiumMembership.backgroundColor.default},
        ${theme.components.PremiumMembership.backgroundColor.default}
      ),
      url(${defaultBackground})
    `,
  },
  silver: {
    backgroundImage: `url(${silverBackground})`,
    backgroundColor: theme.components.PremiumMembership.backgroundColor.silver,
    color: theme.components.PremiumMembership.color.silver,
  },
  silverDetailed: {
    backgroundImage: `
      linear-gradient(
        transparent,
        ${theme.components.PremiumMembership.backgroundColor.silver},
        ${theme.components.PremiumMembership.backgroundColor.silver}
      ),
      url(${silverBackground})
    `,
  },
  gold: {
    backgroundImage: `url(${goldBackground})`,
    backgroundColor: theme.components.PremiumMembership.backgroundColor.gold,
    color: theme.components.PremiumMembership.color.gold,
  },
  goldDetailed: {
    backgroundImage: `
      linear-gradient(
        transparent,
        ${theme.components.PremiumMembership.backgroundColor.gold},
        ${theme.components.PremiumMembership.backgroundColor.gold}
      ),
      url(${goldBackground})
    `,
  },
  faded: {
    opacity: .7,
  },
  topSpace: {
    marginTop: theme.spacing() * 2,
  },
  fullWidth: {
    width: '100%',
  },
  padded: {
    padding: '12px 0',
  },
  topPadded: {
    paddingTop: 12,
  },
  closeButtonContainer: {
    position: 'absolute',
    right: 0,
    top: 0,
  }
})

type InjectedProps = Props & WithStyles<typeof styles>

const PremiumMembership: React.SFC<InjectedProps> = ({
  onClose,
  classes,
  className,
  membership,
  isDetailed,
  isShowExtend,
  activeMembership,
}) => {
  const displayedMembership = membership || activeMembership
  const isMembershipsEquals = membership && activeMembership && membership.type === activeMembership.type

  const isDefault = !displayedMembership
  const isSilver = displayedMembership && displayedMembership.type === StorePremiumProduct.Type.SILVER
  const isGold = displayedMembership && displayedMembership.type === StorePremiumProduct.Type.GOLD

  return (
    <Grid
      className={classNames(className, classes.root, {
        [classes.detailed]: isDetailed,
        [classes.default]: isDefault,
        [classes.defaultDetailed]: isDefault && isDetailed,
        [classes.silver]: isSilver,
        [classes.silverDetailed]: isSilver && isDetailed,
        [classes.gold]: isGold,
        [classes.goldDetailed]: isGold && isDetailed,
      })}
      direction="column"
      alignItems="center"
      wrap="nowrap"
      container
    >
      {onClose && (
        <Grid className={classes.closeButtonContainer} item>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      )}

      <Grid item>
        <Typography className={classes.faded} color="inherit" variant="caption">
          <T id="store_premium_promo_subtitle" />
        </Typography>
      </Grid>

      <Grid item>
        <Typography className={classes.padded} color="inherit" variant="h5">
          {displayedMembership ? displayedMembership.title : (
            <T id="store_premium_promo_title" />
          )}
        </Typography>
      </Grid>

      {isMembershipsEquals && (
        <Grid item>
          <Grid direction="row" alignItems="flex-end" container>
            <Grid item>
              <Typography color="inherit" variant="h4">
                {(activeMembership && activeMembership.duration) || 0}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.faded} color="inherit" variant="caption">
                {
                  (activeMembership && activeMembership.duration) || 0 > 1
                    ? <T id="store_premium_remainingtime_plural" />
                    : <T id="store_premium_remainingtime_singular" />
                }
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      )}

      {!isMembershipsEquals && (
        <Grid item>
          <Typography className={classes.faded} color="inherit" variant="caption">
            {displayedMembership ? displayedMembership!.summary : (
              <T id="store_premium_promo_message" />
            )}
          </Typography>
        </Grid>
      )}

      {isDefault && (
        <Grid item>
          <NavigateToStoreButton className={classes.topSpace} />
        </Grid>
      )}

      {!isDefault && (isDetailed || isShowExtend) && isMembershipsEquals && (
        <Grid item>
          <UpgradeMembershipButton variant="extend" className={classes.topSpace} membership={membership!} />
        </Grid>
      )}

      {!isDefault && (isDetailed || isShowExtend) && !isMembershipsEquals && (
        <Grid item>
          <UpgradeMembershipButton variant="upgrade" className={classes.topSpace} membership={membership!} />
        </Grid>
      )}

      {!isDefault && !isDetailed && !isShowExtend && (
        <Grid item>
          <MoreInfoButton className={classes.topSpace} membership={membership!} />
        </Grid>
      )}

      {isDetailed && membership && (
        <Grid className={classes.fullWidth} item>
          <PremiumMembershipDetails className={classes.topPadded} details={membership.detailItem} />
        </Grid>
      )}
    </Grid>
  )
}

export default withStyles(styles)(PremiumMembership)
